if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      lastPicker = null;
      confirmedPrice = null;
      remoteSectionId = null;
      previousSubmit = null;
      guardVariantAction = (event) => {
        if (!this.previousSubmit && !this.hasAttribute('data-zb-unavailable')) return;
        const form = this.productForm?.querySelector('form');
        const action = event.type === 'submit' ? event.target === form :
          event.target instanceof Element && form?.contains(event.target.closest('[name="add"], [data-st-checkout], .shopify-payment-button__button'));
        if (action) { event.preventDefault(); event.stopImmediatePropagation(); }
      };
      onHistoryRestore = (event) => {
        if (!event.persisted) return;
        // The browser can restore radio state separately from the last response.
        this.abortController?.abort();
        this.abortController = null;
        this.pendingRequestUrl = null;
        this.restoreVariant(false);
      };
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.rememberVariant();
        window.addEventListener('pageshow', this.onHistoryRestore);
        this.addEventListener('submit',this.guardVariantAction,true);
        this.addEventListener('click',this.guardVariantAction,true);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.reconcileEarlyChoice();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onHistoryRestore);
        this.removeEventListener('submit',this.guardVariantAction,true);
        this.removeEventListener('click',this.guardVariantAction,true);
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          if (newNode.querySelector('[data-shopify="payment-button"], shopify-accelerated-checkout')) {
            window?.Shopify?.PaymentButton?.init();
          }
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (!this.previousSubmit) {
          const button = this.productForm?.querySelector('[name="add"]');
          this.previousSubmit = { disabled: button?.disabled ?? true, text: button?.querySelector('span')?.textContent?.trim() };
        }
        this.querySelector('[data-zb-feedback]')?.remove();
        this.priceSlot?.setAttribute('aria-busy', 'true');
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedOptionValues),
        });
      }

      setSubmitState(disabled,text) {
        const form = this.productForm;
        if (typeof form?.toggleSubmitButton === 'function') return form.toggleSubmitButton(disabled,text);
        const button = form?.querySelector('button[name="add"]');
        if (button) {
          button.disabled = disabled;
          const label = button.querySelector('span');
          if (label && (text || !disabled)) label.textContent = disabled ? text : window.variantStrings.addToCart;
        }
      }

      resetProductFormState() {
        this.setSubmitState(true);
        this.productForm?.handleErrorMessage?.();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const incoming = html.querySelector(selector);
          if (!incoming || (updateFullPage && (!html.querySelector('main') || !html.querySelector('title')))) throw Error('Incomplete product response');
          const variant = incoming.querySelector('variant-selects') ? this.getSelectedVariant(incoming) : null;
          this.productModal?.remove();
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      get priceSlot() {
        return this.querySelector('[data-zb-price-slot]') || this.querySelector('[id="price-' + this.dataset.section + '"]');
      }

      rememberVariant() {
        const picker = this.variantSelectors;
        this.lastPicker = picker?.cloneNode(true) || null;
        if (!picker) return;
        const slot = this.priceSlot;
        const offer = this.querySelector('[data-zb-variant-promo]');
        this.confirmedPrice = {
          html:slot?.innerHTML,
          id:this.productForm?.querySelector('input[name="id"]')?.value,
          unavailable:this.hasAttribute('data-zb-unavailable'),
          offer:offer ? {html:offer.innerHTML,hidden:offer.hidden} : null,
          url:location.href
        };
      }

      reconcileEarlyChoice() {
        const picker = this.variantSelectors;
        if (!picker || !this.lastPicker) return;
        let variant;
        try { variant = this.getSelectedVariant(this); } catch (_) { return; }
        if (!variant?.options) return;
        // Native radios already react while deferred theme JS is in transit.
        // Keep the server choice for rollback, and honour any early user choice.
        const groups = [...picker.querySelectorAll('fieldset,select')];
        const copies = this.lastPicker.querySelectorAll('fieldset,select');
        let early = null;
        variant.options.forEach((value,i) => {
          const group = groups[i], copy = copies[i];
          if (!group || !copy) return;
          const picked = group.matches('select') ? group.selectedOptions[0] : group.querySelector('input:checked');
          if (!picked || picked.value === String(value)) return;
          if (!early) early = group.matches('select') ? group : picked;
          if (copy.matches('select')) {
            for (const option of copy.options) option.selected = option.defaultSelected = option.value === String(value);
          } else {
            copy.querySelectorAll('input[type=radio]').forEach(input => {input.checked = input.defaultChecked = input.value === String(value);});
          }
          const label = copy.querySelector('[data-selected-value]');
          if (label && !label.children.length) label.textContent = value;
        });
        if (!early) return;
        const target = early;
        customElements.whenDefined('product-form').then(() => {
          if (!this.isConnected || this.variantSelectors !== picker || this.previousSubmit) return;
          target.dispatchEvent(new Event('change',{bubbles:true}));
        });
      }

      restoreVariant(notify) {
        const picker = this.variantSelectors;
        const active = document.activeElement;
        const focusInPicker = picker?.contains(active);
        if (picker && this.lastPicker) {
          picker.replaceWith(this.lastPicker.cloneNode(true));
          if (focusInPicker) this.variantSelectors.querySelector('input:checked, select')?.focus({preventScroll:true});
        }
        const slot = this.priceSlot;
        slot?.removeAttribute('aria-busy');
        const state = this.confirmedPrice;
        if (state) {
          if (slot && state.html !== undefined && slot.innerHTML !== state.html) slot.innerHTML = state.html;
          this.toggleAttribute('data-zb-unavailable', state.unavailable);
          const offer = this.querySelector('[data-zb-variant-promo]');
          if (offer && state.offer) {
            offer.hidden = state.offer.hidden;
            if (offer.innerHTML !== state.offer.html) offer.innerHTML = state.offer.html;
          }
          if (state.id !== undefined && this.productForm?.querySelector('input[name="id"]')?.value !== state.id) this.updateVariantInputs(state.id);
          if (this.dataset.updateUrl === 'true' && this.previousSubmit && new URL(location.href).searchParams.get('variant') !== new URL(state.url).searchParams.get('variant')) {
            history.replaceState(history.state,'',state.url);
          }
        }
        if (this.previousSubmit) this.setSubmitState(this.previousSubmit.disabled, this.previousSubmit.text);
        this.previousSubmit = null;
        if (notify && slot) {
          slot.querySelector('[data-zb-feedback]')?.remove();
          const msg = document.createElement('small');
          msg.setAttribute('data-zb-feedback', '');
          msg.textContent = slot.dataset.zbVariantError;
          slot.append(msg);
        }
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        let timedOut = false;
        let timeout;
        const startClock = () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 12000);
        };
        startClock();
        try {
          const endpoint = new URL(requestUrl,location.href);
          const isMainSection = endpoint.searchParams.has('section_id') && this.id.startsWith('MainProduct-') && !this.dataset.originalSection;
          let response = await fetch(endpoint, { signal: controller.signal });
          if (isMainSection && (response.status === 404 || response.status === 410)) {
            // A published replacement theme has new dynamic section IDs. Retry
            // the same option IDs through the canonical product, never a cart POST.
            endpoint.searchParams.delete('section_id');
            startClock();
            response = await fetch(endpoint, {signal:controller.signal});
          }
          if (!response.ok) throw Error('Product section HTTP ' + response.status);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (controller.signal.aborted || controller !== this.abortController || !this.isConnected) return;
          const remote = isMainSection ? this.alignMainSection(html) : null;
          const focus = document.activeElement;
          callback(html);
          if (remote) this.remoteSectionId = remote;
          this.rememberVariant();
          this.previousSubmit = null;
          if (focus?.id === targetId && (document.activeElement === document.body || document.activeElement === focus)) {
            document.getElementById(targetId)?.focus({preventScroll:true});
          }
        } catch (error) {
          if (this.isConnected && controller === this.abortController && (!controller.signal.aborted || timedOut)) this.restoreVariant(true);
        } finally {
          clearTimeout(timeout);
          if (this.abortController === controller) {
            this.abortController = null;
            this.pendingRequestUrl = null;
            this.priceSlot?.removeAttribute('aria-busy');
          }
        }
      }

      alignMainSection(doc) {
        const main = Array.from(doc.querySelectorAll('product-info[id^="MainProduct-"]')).find(el =>
          el.dataset.productId === this.dataset.productId && el.id === 'MainProduct-' + el.dataset.section);
        if (!main) throw Error('Missing main product');
        const remote = main.dataset.section;
        if (remote !== this.sectionId) {
          // Keep the existing form, customer input, event handlers and DOM IDs.
          // Only translate references inside Shopify's detached incoming section.
          const attrs = ['id','for','form','data-section','data-media-id','aria-controls','aria-labelledby','aria-describedby'];
          for (const el of [main,...main.querySelectorAll('[id],[for],[form],[data-section],[data-media-id],[aria-controls],[aria-labelledby],[aria-describedby]')]) {
            for (const name of attrs) {
              const value = el.getAttribute(name);
              if (value?.includes(remote)) el.setAttribute(name,value.replaceAll(remote,this.sectionId));
            }
          }
        }
        return remote;
      }

      getSelectedVariant(node) {
        const data = node?.querySelector('variant-selects [data-selected-variant]');
        if (!data) throw Error('Missing variant data');
        const variant = JSON.parse(data.textContent);
        if (variant !== null && (!variant?.id || typeof variant.available !== 'boolean')) throw Error('Invalid variant');
        return variant;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const dest = new URL(url, location.href);
        dest.searchParams.delete('variant');
        dest.searchParams.delete('sections');
        dest.searchParams.delete('section_id');
        dest.searchParams.delete('option_values');
        if (dest.origin === location.origin) {
          // Keep the theme preview and market context when requesting a section.
          const current = new URLSearchParams(location.search);
          for (const key of ['preview_theme_id','preview_key','preview_token','_fd','pb','market','country']) {
            const value = current.get(key);
            if (value !== null && !dest.searchParams.has(key)) dest.searchParams.set(key, value);
          }
        }
        if (!shouldFetchFullPage) dest.searchParams.set('section_id',this.remoteSectionId || this.sectionId);
        if (optionValues.length) dest.searchParams.set('option_values',optionValues.join(','));
        return dest.href;
      }

      updateOptionValues(node) {
        const next = node.querySelector('variant-selects');
        if (!next) return;
        if (this.id.startsWith('MainProduct-') && !this.dataset.originalSection) {
          // A picker contains form controls and JSON. There must never be two copies with the same name/ID.
          this.variantSelectors.replaceWith(document.importNode(next,true));
        } else {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, next, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, requested) {
        return (html) => {
          const scope = Array.from(html.querySelectorAll('product-info')).find(node =>
            node.dataset.section === this.sectionId && node.dataset.productId === this.dataset.productId
          );
          if (!scope) throw Error('Unexpected product section');
          const variant = this.getSelectedVariant(scope);
          const selected = Array.from(scope.querySelectorAll('variant-selects input:checked, variant-selects option:checked'),
            el => el.dataset.optionValueId);
          if (selected.length !== requested.length || selected.some((id,i) => id !== requested[i])) throw Error('Mismatched options');
          const sourcePrice = scope.querySelector('[id="price-' + this.sectionId + '"]');
          if (this.priceSlot && variant && !sourcePrice?.querySelector('.price-item')?.textContent.trim()) throw Error('Missing price');
          const submit = scope.querySelector('[id="ProductSubmitButton-' + this.sectionId + '"]');
          if (variant && this.productForm && !submit) throw Error('Missing submit state');

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(scope);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          const offer = this.querySelector('[data-zb-variant-promo]');
          const nextOffer = scope.querySelector('[data-zb-variant-promo]');
          if (offer && nextOffer) { offer.innerHTML = nextOffer.innerHTML; offer.hidden = nextOffer.hidden; }
          if (!variant) {
            if (offer) offer.hidden = true;
            this.setUnavailable();
            return;
          }

          this.removeAttribute('data-zb-unavailable');
          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.setSubmitState(
            !variant.available || !submit || submit.disabled,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        const target = new URL(url, location.href);
        const address = target.pathname === location.pathname ? new URL(location.href) : target;
        const preview = new URLSearchParams(location.search);
        for (const k of ['preview_theme_id','preview_key','preview_token','_fd','pb']) if (preview.has(k)) address.searchParams.set(k,preview.get(k));
        for (const k of ['option_values','sections','section_id','variant']) address.searchParams.delete(k);
        if (variantId) address.searchParams.set('variant',variantId);
        window.history.replaceState(window.history.state,'',address);
      }

      setUnavailable() {
        this.setSubmitState(true, window.variantStrings.unavailable);

        this.setAttribute('data-zb-unavailable','');
        const slot = this.priceSlot;
        if (slot) {
          slot.classList.remove('hidden');
          slot.textContent = window.variantStrings.unavailable;
        }
        const selectors = ['Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
